import { useEffect, useState, type CSSProperties } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { Field, IssueList, ListEditor, TextArea } from '../components/ui';
import { api } from '../lib/api';
import type { Booklet, Comment, Currency } from '../lib/types';
import { useProjectContext } from './ProjectLayout';

const CURRENCIES: Currency[] = ['EUR', 'USD', 'GBP', 'CHF', 'IDR'];

/** Which page a section feeds, for the "switched off" notice. */
const SECTION_PAGE: Partial<Record<string, string>> = {
  route: 'routes', schedule: 'schedule', experience: 'experience', packages: 'packages',
  inclusions: 'inclusions', deposits: 'deposits', contact: 'contact',
};

/** A section counts as done when the booklet has what that page needs. */
function sectionDone(id: string, b: Booklet): boolean {
  switch (id) {
    case 'tour': return !!b.tour.title.trim() && !!b.tour.tagline.trim() && b.tour.durationDays > 0;
    case 'route': return b.route.cities.length >= 2 && b.route.totalDistanceKm != null;
    case 'schedule': return b.schedule.length > 0 && b.schedule.length === b.tour.durationDays;
    case 'days': return b.days.length > 0 && b.days.every((d) => d.title.trim() && d.body.trim());
    case 'experience': return b.experience.length > 0;
    case 'packages': return b.packages.length > 0 && b.packages.every((p) => !p.price.pending && p.price.amount != null);
    case 'inclusions': return b.included.length > 0;
    case 'deposits': return b.deposits.length > 0;
    case 'contact': return !!b.contact.email && b.contact.phones.length > 0;
    default: return false;
  }
}

const SECTIONS = [
  ['tour', 'Tour info'],
  ['route', 'Route'],
  ['schedule', 'Schedule'],
  ['days', 'Day pages'],
  ['experience', 'Experience'],
  ['packages', 'Packages & pricing'],
  ['inclusions', 'Included / not included'],
  ['deposits', 'Deposits'],
  ['contact', 'Contact'],
] as const;

type SectionId = (typeof SECTIONS)[number][0];

export function ContentPage() {
  const { project, update } = useProjectContext();
  const [params, setParams] = useSearchParams();
  const wanted = params.get('section') as SectionId | null;
  const wantedDay = Number(params.get('day')) || null;
  const [section, setSectionState] = useState<SectionId>(wanted ?? 'tour');
  const [comments, setComments] = useState<Comment[]>([]);
  useEffect(() => { if (wanted) setSectionState(wanted); }, [wanted]);
  useEffect(() => { api.listComments(project.id).then(setComments).catch(() => {}); }, [project.id]);
  const b = project.content;
  const cur = b.tour.currency ?? 'EUR';
  const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(/[^\d.-]/g, '')));

  const index = SECTIONS.findIndex(([id]) => id === section);
  const setSection = (id: SectionId) => {
    setSectionState(id);
    setParams({ section: id }, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const done = SECTIONS.filter(([id]) => sectionDone(id, b)).length;
  const inSection = (path: string | null | undefined) => !!path && (path === section || path.startsWith(`${section}.`));
  const openHere = comments.filter((c) => !c.resolved && inSection(c.fieldPath));
  const hiddenPage = SECTION_PAGE[section] && b.hiddenPages.includes(SECTION_PAGE[section]!);
  const blockers = (project.issues ?? []).filter((i) => i.level === 'blocker' && inSection(i.field));

  return (
    <>
      <div className="stepper">
        <div className="sub" style={{ marginBottom: 8 }}>
          {done} of {SECTIONS.length} sections filled in. Work top to bottom, or jump to any section.
        </div>
        <div className="row" style={{ gap: 6 }}>
          {SECTIONS.map(([id, label], i) => (
            <button
              key={id}
              className={`btn small step-btn ${section === id ? 'primary' : ''} ${sectionDone(id, b) ? 'done' : ''}`}
              onClick={() => setSection(id)}
            >
              <span className="check">{sectionDone(id, b) ? '✓' : i + 1}</span> {label}
            </button>
          ))}
        </div>
      </div>

      {hiddenPage && (
        <div className="notice" style={{ marginBottom: 12 }}>
          This page is switched off for this booklet, so it will not print. Turn it back on in <NavLink to="../design">step 3 &middot; Pages</NavLink>.
        </div>
      )}

      {openHere.length > 0 && (
        <div className="panel comment-notes">
          <h2 className="section">Reviewer comments on this section</h2>
          {openHere.map((c) => (
            <div className="comment-note" key={c.id}>
              <b>{c.authorName}</b>{c.page ? <span className="sub"> &middot; page {c.page}</span> : null}: {c.body}
            </div>
          ))}
          <div className="sub" style={{ fontSize: 12 }}>Mark them resolved from the comments panel in step 4.</div>
        </div>
      )}

      {blockers.length > 0 && (
        <div className="panel">
          <h2 className="section">Still missing here</h2>
          <IssueList issues={blockers} limit={6} />
        </div>
      )}

      {section === 'tour' && (
        <div className="panel">
          <h2 className="section">Tour info</h2>
          <div className="grid-2">
            <Field label="Title" value={b.tour.title} onChange={(v) => update((d) => ({ ...d, tour: { ...d.tour, title: v } }))} hint="Shown huge on the cover" />
            <Field label="Eyebrow" value={b.tour.eyebrow} onChange={(v) => update((d) => ({ ...d, tour: { ...d.tour, eyebrow: v } }))} />
          </div>
          <TextArea label="Tagline" value={b.tour.tagline} rows={2} onChange={(v) => update((d) => ({ ...d, tour: { ...d.tour, tagline: v } }))} />
          <ListEditor
            label="Cover headline (one line each)"
            items={b.tour.coverHeadline}
            addLabel="line"
            onChange={(items) => update((d) => ({ ...d, tour: { ...d.tour, coverHeadline: items } }))}
          />
          <div className="grid-3">
            <Field label="Duration (days)" type="number" value={b.tour.durationDays} onChange={(v) => update((d) => ({ ...d, tour: { ...d.tour, durationDays: Number(v) || 0 } }))} />
            <Field label="Season" value={b.tour.season} placeholder="SUMMER" onChange={(v) => update((d) => ({ ...d, tour: { ...d.tour, season: v } }))} />
            <Field label="Year" type="number" value={b.tour.year} onChange={(v) => update((d) => ({ ...d, tour: { ...d.tour, year: num(v) } }))} hint="Used to turn sheet dates like 28-jun into real dates" />
          </div>
          <div className="grid-3">
            <Field label="Difficulty" type="number" value={b.tour.difficulty.value} onChange={(v) => update((d) => ({ ...d, tour: { ...d.tour, difficulty: { ...d.tour.difficulty, value: Number(v) || 1 } } }))} />
            <Field label="Out of" type="number" value={b.tour.difficulty.of} onChange={(v) => update((d) => ({ ...d, tour: { ...d.tour, difficulty: { ...d.tour.difficulty, of: Number(v) || 5 } } }))} />
            <div className="field">
              <label>Currency</label>
              <select
                value={cur}
                onChange={(e) => {
                  const currency = e.target.value as Currency;
                  update((d) => ({
                    ...d,
                    tour: { ...d.tour, currency },
                    packages: d.packages.map((p) => ({ ...p, price: { ...p.price, currency } })),
                    bikes: d.bikes.map((x) => ({ ...x, price: { ...x.price, currency } })),
                    addons: d.addons.map((x) => ({ ...x, price: { ...x.price, currency } })),
                    deposits: d.deposits.map((x) => ({ ...x, currency })),
                  }));
                }}
              >
                {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <div className="hint">All prices in the booklet use it. Numbers are not converted.</div>
            </div>
          </div>
        </div>
      )}

      {section === 'route' && (
        <div className="panel">
          <h2 className="section">Route</h2>
          <ListEditor
            label="Cities in riding order"
            items={b.route.cities}
            addLabel="city"
            placeholder="Livigno"
            onChange={(items) => update((d) => ({ ...d, route: { ...d.route, cities: items } }))}
          />
          <Field
            label="Total distance (KM)"
            type="number"
            value={b.route.totalDistanceKm}
            onChange={(v) => update((d) => ({ ...d, route: { ...d.route, totalDistanceKm: num(v) } }))}
            hint="The route map is generated from the city list — see the Design tab."
          />
        </div>
      )}

      {section === 'schedule' && (
        <div className="panel">
          <h2 className="section">Schedule</h2>
          <div className="sub" style={{ marginBottom: 10 }}>
            This is the "Discover our schedule" page. Day pages are edited separately.
          </div>
          {b.schedule.map((entry, index) => (
            <div className="row" key={index} style={{ gap: 8, marginBottom: 8 }}>
              <div style={{ width: 70 }}>
                <input
                  type="number"
                  value={entry.day}
                  onChange={(e) => update((d) => patchArray(d, 'schedule', index, { day: Number(e.target.value) || 0 }))}
                  style={inputStyle}
                />
              </div>
              <input
                value={entry.title}
                placeholder="Milan to Livigno"
                onChange={(e) => update((d) => patchArray(d, 'schedule', index, { title: e.target.value }))}
                style={{ ...inputStyle, flex: 1 }}
              />
              <div style={{ width: 110 }}>
                <input
                  type="number"
                  value={entry.distanceKm ?? ''}
                  placeholder="KM"
                  onChange={(e) => update((d) => patchArray(d, 'schedule', index, { distanceKm: e.target.value === '' ? null : Number(e.target.value) }))}
                  style={inputStyle}
                />
              </div>
              <button className="btn small danger" onClick={() => update((d) => ({ ...d, schedule: d.schedule.filter((_, i) => i !== index) }))}>×</button>
            </div>
          ))}
          <div className="row">
            <button
              className="btn small"
              onClick={() => update((d) => ({ ...d, schedule: [...d.schedule, { day: d.schedule.length + 1, title: '', distanceKm: null }] }))}
            >+ Add day</button>
            <button
              className="btn small"
              onClick={() => update((d) => ({
                ...d,
                schedule: d.days.map((day) => ({
                  day: day.day,
                  title: titleCase(day.title),
                  distanceKm: d.schedule.find((s) => s.day === day.day)?.distanceKm ?? null,
                })),
              }))}
            >Rebuild from day pages</button>
          </div>
        </div>
      )}

      {section === 'days' && (
        <div className="panel">
          <h2 className="section">Day pages</h2>
          {b.days.map((day, index) => (
            <details key={index} id={`day-${day.day}`} style={{ borderBottom: '1px solid var(--line)', padding: '10px 0' }} open={wantedDay ? day.day === wantedDay : index === 0}>
              <summary style={{ cursor: 'pointer' }}>
                <span className="mono" style={{ color: 'var(--lime)' }}>DAY {day.day}</span>{' '}
                {day.title || <span className="sub">untitled</span>}
              </summary>
              <div style={{ paddingTop: 12 }}>
                <div className="grid-2">
                  <Field label="Day number" type="number" value={day.day} onChange={(v) => update((d) => patchArray(d, 'days', index, { day: Number(v) || 0 }))} />
                  <Field label="Title" value={day.title} onChange={(v) => update((d) => patchArray(d, 'days', index, { title: v }))} />
                </div>
                <TextArea label="Description" value={day.body} rows={5} onChange={(v) => update((d) => patchArray(d, 'days', index, { body: v }))} />
                <button className="btn small danger" onClick={() => update((d) => ({ ...d, days: d.days.filter((_, i) => i !== index) }))}>Remove day</button>
              </div>
            </details>
          ))}
          <button
            className="btn small"
            style={{ marginTop: 12 }}
            onClick={() => update((d) => ({
              ...d,
              days: [...d.days, {
                day: d.days.length + 1,
                title: '',
                body: '',
                photos: [{ slot: 'main', assetId: null, focalX: .5, focalY: .5, zoom: 1 }],
              }],
            }))}
          >+ Add day page</button>
        </div>
      )}

      {section === 'experience' && (
        <div className="panel">
          <h2 className="section">Wheels and Wonders experience</h2>
          {b.experience.map((item, index) => (
            <div className="row" key={index} style={{ gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
              <input
                value={item.label}
                placeholder="TOUR GUIDE"
                onChange={(e) => update((d) => patchArray(d, 'experience', index, { label: e.target.value }))}
                style={{ ...inputStyle, width: 220 }}
              />
              <input
                value={item.description}
                placeholder="Guided tours led by experienced riders"
                onChange={(e) => update((d) => patchArray(d, 'experience', index, { description: e.target.value }))}
                style={{ ...inputStyle, flex: 1 }}
              />
              <select
                value={item.icon}
                onChange={(e) => update((d) => patchArray(d, 'experience', index, { icon: e.target.value }))}
                style={{ ...inputStyle, width: 130 }}
              >
                {['guide', 'hotel', 'dinner', 'laundry', 'van', 'camera'].map((icon) => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
              <button className="btn small danger" onClick={() => update((d) => ({ ...d, experience: d.experience.filter((_, i) => i !== index) }))}>×</button>
            </div>
          ))}
          <button
            className="btn small"
            onClick={() => update((d) => ({ ...d, experience: [...d.experience, { label: '', description: '', icon: 'guide', photo: { slot: 'photo', assetId: null, focalX: .5, focalY: .5, zoom: 1 } }] }))}
          >+ Add experience</button>
        </div>
      )}

      {section === 'packages' && (
        <>
          <div className="panel">
            <h2 className="section">Packages</h2>
            <div className="grid-3">
              {b.packages.map((pkg, index) => (
                <div key={index} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 12 }}>
                  <Field label="Name" value={pkg.name} onChange={(v) => update((d) => patchArray(d, 'packages', index, { name: v }))} />
                  <Field
                    label={`Price (${cur})`}
                    type="number"
                    pending={pkg.price.pending}
                    value={pkg.price.amount}
                    onChange={(v) => update((d) => patchArray(d, 'packages', index, {
                      price: { ...d.packages[index].price, amount: v === '' ? null : Number(v), pending: v === '' },
                    }))}
                    hint={pkg.price.pending ? 'Pending — blocks publishing' : undefined}
                  />
                  <label className="row sub" style={{ gap: 6, marginBottom: 10 }}>
                    <input
                      type="checkbox"
                      checked={pkg.price.pending}
                      style={{ width: 'auto' }}
                      onChange={(e) => update((d) => patchArray(d, 'packages', index, {
                        price: { ...d.packages[index].price, pending: e.target.checked },
                      }))}
                    />
                    Price still pending
                  </label>
                  <ListEditor
                    label="Includes"
                    items={pkg.includes}
                    addLabel="item"
                    onChange={(items) => update((d) => patchArray(d, 'packages', index, { includes: items }))}
                  />
                  <button className="btn small danger" onClick={() => update((d) => ({ ...d, packages: d.packages.filter((_, i) => i !== index) }))}>Remove package</button>
                </div>
              ))}
            </div>
            <button
              className="btn small"
              style={{ marginTop: 12 }}
              onClick={() => update((d) => ({
                ...d,
                packages: [...d.packages, {
                  code: `pkg${d.packages.length + 1}`, name: 'NEW PACKAGE', pax: 1,
                  price: { amount: null, currency: cur, pending: true },
                  priceLabel: 'NORMAL PRICE', includes: [], hotel: 'shared',
                }],
              }))}
            >+ Add package</button>
          </div>

          <div className="panel">
            <h2 className="section">Bikes &amp; add-ons</h2>
            {b.bikes.map((bike, index) => (
              <div className="row" key={index} style={{ gap: 8, marginBottom: 8 }}>
                <input value={bike.brand} onChange={(e) => update((d) => patchArray(d, 'bikes', index, { brand: e.target.value }))} style={{ ...inputStyle, width: 140 }} />
                <input value={bike.model} onChange={(e) => update((d) => patchArray(d, 'bikes', index, { model: e.target.value }))} style={{ ...inputStyle, width: 160 }} />
                <input
                  type="number"
                  value={bike.price.amount ?? ''}
                  placeholder={cur}
                  onChange={(e) => update((d) => patchArray(d, 'bikes', index, {
                    price: { ...d.bikes[index].price, amount: e.target.value === '' ? null : Number(e.target.value), pending: e.target.value === '' },
                  }))}
                  style={{ ...inputStyle, width: 140 }}
                />
                <button className="btn small danger" onClick={() => update((d) => ({ ...d, bikes: d.bikes.filter((_, i) => i !== index) }))}>×</button>
              </div>
            ))}
            <button className="btn small" onClick={() => update((d) => ({ ...d, bikes: [...d.bikes, { brand: 'BMW', model: '', price: { amount: null, currency: cur, pending: true } }] }))}>+ Add bike</button>

            <div className="divider-line" />

            {b.addons.map((addon, index) => (
              <div className="row" key={index} style={{ gap: 8, marginBottom: 8 }}>
                <input value={addon.name} onChange={(e) => update((d) => patchArray(d, 'addons', index, { name: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                <input value={addon.unit} onChange={(e) => update((d) => patchArray(d, 'addons', index, { unit: e.target.value }))} style={{ ...inputStyle, width: 140 }} />
                <input
                  type="number"
                  value={addon.price.amount ?? ''}
                  onChange={(e) => update((d) => patchArray(d, 'addons', index, {
                    price: { ...d.addons[index].price, amount: e.target.value === '' ? null : Number(e.target.value), pending: e.target.value === '' },
                  }))}
                  style={{ ...inputStyle, width: 140 }}
                />
                <button className="btn small danger" onClick={() => update((d) => ({ ...d, addons: d.addons.filter((_, i) => i !== index) }))}>×</button>
              </div>
            ))}
            <button className="btn small" onClick={() => update((d) => ({ ...d, addons: [...d.addons, { name: '', unit: 'per person', price: { amount: null, currency: cur, pending: true } }] }))}>+ Add add-on</button>
          </div>
        </>
      )}

      {section === 'inclusions' && (
        <div className="panel">
          <h2 className="section">Included / not included</h2>
          <div className="grid-2">
            <ListEditor label="Included" items={b.included} addLabel="item" onChange={(items) => update((d) => ({ ...d, included: items }))} />
            <ListEditor label="Not included" items={b.notIncluded} addLabel="item" onChange={(items) => update((d) => ({ ...d, notIncluded: items }))} />
          </div>
          <div className="grid-2">
            <ListEditor label="Exclusions (printed under the packages)" items={b.exclusions} addLabel="item" onChange={(items) => update((d) => ({ ...d, exclusions: items }))} />
            <ListEditor label="Footnotes" items={b.notes} addLabel="note" onChange={(items) => update((d) => ({ ...d, notes: items }))} />
          </div>
        </div>
      )}

      {section === 'deposits' && (
        <div className="panel">
          <h2 className="section">Deposits</h2>
          <div className="grid-3">
            {b.deposits.map((deposit, index) => (
              <div key={index} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 12 }}>
                <Field label="Name" value={deposit.name} onChange={(v) => update((d) => patchArray(d, 'deposits', index, { name: v }))} />
                <Field label={`Deposit (${cur})`} type="number" value={deposit.amount} onChange={(v) => update((d) => patchArray(d, 'deposits', index, { amount: v === '' ? null : Number(v) }))} />
                <Field label={`Additional (${cur})`} type="number" value={deposit.surcharge} onChange={(v) => update((d) => patchArray(d, 'deposits', index, { surcharge: v === '' ? null : Number(v) }))} />
                <ListEditor label="Points" items={deposit.points} addLabel="point" onChange={(items) => update((d) => patchArray(d, 'deposits', index, { points: items }))} />
                <button className="btn small danger" onClick={() => update((d) => ({ ...d, deposits: d.deposits.filter((_, i) => i !== index) }))}>Remove</button>
              </div>
            ))}
          </div>
          <button
            className="btn small"
            style={{ marginTop: 12 }}
            onClick={() => update((d) => ({
              ...d,
              deposits: [...d.deposits, { code: `dep${d.deposits.length + 1}`, name: '', amount: null, surcharge: null, currency: cur, points: [] }],
            }))}
          >+ Add deposit</button>
        </div>
      )}

      {section === 'contact' && (
        <div className="panel">
          <h2 className="section">Contact</h2>
          <div className="grid-2">
            <Field label="Email" value={b.contact.email} onChange={(v) => update((d) => ({ ...d, contact: { ...d.contact, email: v } }))} />
            <Field label="Website" value={b.contact.website} onChange={(v) => update((d) => ({ ...d, contact: { ...d.contact, website: v } }))} />
            <Field label="Instagram" value={b.contact.instagram} onChange={(v) => update((d) => ({ ...d, contact: { ...d.contact, instagram: v } }))} />
            <Field label="TikTok" value={b.contact.tiktok} onChange={(v) => update((d) => ({ ...d, contact: { ...d.contact, tiktok: v } }))} />
          </div>
          <TextArea label="Address" value={b.contact.address} rows={2} onChange={(v) => update((d) => ({ ...d, contact: { ...d.contact, address: v } }))} />
          {b.contact.phones.map((phone, index) => {
            const guess = regionForNumber(phone.number);
            const mismatch = guess && phone.region.trim() && guess.toLowerCase() !== phone.region.trim().toLowerCase();
            const setPhone = (patch: Partial<{ region: string; number: string }>) => update((d) => ({
              ...d,
              contact: { ...d.contact, phones: d.contact.phones.map((p, i) => (i === index ? { ...p, ...patch } : p)) },
            }));
            return (
              <div key={index} style={{ marginBottom: 8 }}>
                <div className="row" style={{ gap: 8 }}>
                  <input
                    value={phone.region}
                    placeholder="Indonesia"
                    list="phone-regions"
                    onChange={(e) => setPhone({ region: e.target.value })}
                    style={{ ...inputStyle, width: 180 }}
                  />
                  <input
                    value={phone.number}
                    placeholder="+62 851-3879-4588"
                    onChange={(e) => {
                      const number = e.target.value;
                      // An empty region follows the dialling code, so a +62 number
                      // never ends up labelled Europe.
                      const region = phone.region.trim() ? phone.region : regionForNumber(number);
                      setPhone({ number, region });
                    }}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    className="btn small danger"
                    onClick={() => update((d) => ({ ...d, contact: { ...d.contact, phones: d.contact.phones.filter((_, i) => i !== index) } }))}
                  >×</button>
                </div>
                {mismatch && (
                  <div className="hint-line warn-text" style={{ marginLeft: 2 }}>
                    {phone.number.trim().slice(0, 3)} is {guess}, not {phone.region}.{' '}
                    <button className="btn small ghost" style={{ padding: '1px 6px' }} onClick={() => setPhone({ region: guess })}>
                      Use {guess}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <datalist id="phone-regions">
            <option value="Indonesia" />
            <option value="Europe" />
            <option value="Asia" />
            <option value="Worldwide" />
          </datalist>
          <button
            className="btn small"
            onClick={() => update((d) => ({ ...d, contact: { ...d.contact, phones: [...d.contact.phones, { region: '', number: '' }] } }))}
          >+ Add phone</button>
        </div>
      )}

      <div className="row step-nav">
        <button className="btn" disabled={index <= 0} onClick={() => setSection(SECTIONS[index - 1][0])}>
          &larr; {index > 0 ? SECTIONS[index - 1][1] : 'Previous'}
        </button>
        <div style={{ flex: 1 }} />
        {index < SECTIONS.length - 1 ? (
          <button className="btn primary" onClick={() => setSection(SECTIONS[index + 1][0])}>
            Next: {SECTIONS[index + 1][1]} &rarr;
          </button>
        ) : (
          <NavLink to="../design" className="btn primary">Next: photos &amp; pages &rarr;</NavLink>
        )}
      </div>
    </>
  );
}

const inputStyle: CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: 7,
  padding: '9px 11px',
  outline: 'none',
  width: '100%',
};

/** Replace one entry of an array field without mutating the rest. */
function patchArray<K extends 'schedule' | 'days' | 'experience' | 'packages' | 'bikes' | 'addons' | 'deposits'>(
  booklet: Booklet,
  key: K,
  index: number,
  patch: Partial<Booklet[K][number]>,
): Booklet {
  const list = booklet[key] as Booklet[K];
  return {
    ...booklet,
    [key]: list.map((item, i) => (i === index ? { ...item, ...patch } : item)),
  };
}

function titleCase(text: string): string {
  return text.toLowerCase().replace(/(^|\s)([a-z])/g, (_, p, c) => p + c.toUpperCase());
}

/**
 * Which region a phone number belongs to, from its dialling code. Used to fill
 * the region in and to flag a number that sits under the wrong label.
 */
function regionForNumber(raw: string): string {
  const number = raw.replace(/[^\d+]/g, '');
  const code = number.startsWith('+') ? number.slice(1) : number.startsWith('00') ? number.slice(2) : '';
  if (!code) return '';
  const starts = (...prefixes: string[]) => prefixes.some((p) => code.startsWith(p));
  if (starts('62')) return 'Indonesia';
  if (starts('60')) return 'Malaysia';
  if (starts('65')) return 'Singapore';
  if (starts('61')) return 'Australia';
  if (starts('1')) return 'North America';
  if (starts('30', '31', '32', '33', '34', '351', '352', '353', '354', '356', '357', '358', '359',
    '36', '370', '371', '372', '39', '40', '41', '420', '421', '43', '44', '45', '46', '47', '48', '49')) return 'Europe';
  return '';
}
